import { useState } from 'react';
import { Download, X, Loader2 } from 'lucide-react';
const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
const EMAIL_KEY = 'taxera_guide_email';
const readSaved = () => {
  try {
    return window.localStorage.getItem(EMAIL_KEY) || '';
  } catch (e) {
    return '';
  }
};
const saveEmail = (email) => {
  try {
    window.localStorage.setItem(EMAIL_KEY, email);
  } catch (e) {
    /* storage can be blocked: the download still works */
  }
};
const forgetEmail = () => {
  try {
    window.localStorage.removeItem(EMAIL_KEY);
  } catch (e) {
    /* ignore */
  }
};
// "Download as PDF" button. Asks for an email address once, then downloads the guide.
// After the first time, the email is remembered on this device and further guides download in one click.
const EinvoicingGuideDownload = ({ c }) => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const download = async (address) => {
    setBusy(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/einvoicing-guides/${c.slug}/download`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: address, consent: true }),
      });
      if (res.status === 400 || res.status === 422) {
        forgetEmail();
        setOpen(true);
        setError('Please check your email address and try again.');
        return;
      }
      if (!res.ok) throw new Error(`status ${res.status}`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Taxera-e-Invoicing-${c.name.replace(/\s+/g, '-')}-guide.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(url), 5000);
      saveEmail(address);
      setOpen(false);
    } catch (e) {
      // The email is already saved on our side. Fall back to the print view so the visitor still gets the guide.
      saveEmail(address);
      setOpen(false);
      window.open(`/print/e-invoicing/${c.slug}?autoprint=1`, '_blank', 'noopener');
    } finally {
      setBusy(false);
    }
  };
  const onClick = () => {
    const saved = readSaved();
    if (saved) {
      download(saved);
    } else {
      setError('');
      setOpen(true);
    }
  };
  const onSubmit = (e) => {
    e.preventDefault();
    const address = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (!consent) {
      setError('Please tick the box to continue.');
      return;
    }
    download(address);
  };
  return (
    <>
      <button
        type="button"
        onClick={onClick}
        disabled={busy && !open}
        className="inline-flex items-center gap-2 border border-slate-300 hover:border-[#0066CC] hover:text-[#0066CC] text-slate-800 font-semibold px-6 py-3 rounded-xl transition-colors disabled:opacity-60"
        data-testid={`einv-guide-download-${c.slug}`}
      >
        {busy && !open ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
        Download as PDF
      </button>
      {open && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 px-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="einv-guide-title"
          data-testid="einv-guide-modal"
          onClick={(e) => {
            if (e.target === e.currentTarget && !busy) setOpen(false);
          }}
        >
          <div className="relative w-full max-w-md rounded-2xl bg-white p-7 shadow-2xl">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="absolute right-4 top-4 text-slate-400 hover:text-slate-700"
              aria-label="Close"
              disabled={busy}
            >
              <X className="w-5 h-5" />
            </button>
            <h2 id="einv-guide-title" className="text-xl font-bold text-slate-900 pr-6">
              Download the {c.name} e-Invoicing guide
            </h2>
            <p className="mt-2 text-sm text-slate-600 leading-relaxed">
              Enter your email and the PDF downloads straight away. It has the same content as this page. Next time, other country guides download in one click on this device.
            </p>
            <form onSubmit={onSubmit} className="mt-5 space-y-4" noValidate>
              <div>
                <label htmlFor="einv-guide-email" className="sr-only">Email address</label>
                <input
                  id="einv-guide-email"
                  type="email"
                  autoComplete="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-xl border border-slate-300 px-4 py-3 text-base focus:outline-none focus:border-[#0066CC] focus:ring-2 focus:ring-[#0066CC]/20"
                  data-testid="einv-guide-email"
                />
              </div>
              <label className="flex items-start gap-3 text-sm text-slate-600 leading-relaxed cursor-pointer">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-slate-300"
                  data-testid="einv-guide-consent"
                />
                <span>
                  I agree that Taxera may store my email address and contact me about e-Invoicing and related topics. See the{' '}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-[#0066CC] underline">privacy policy</a>.
                </span>
              </label>
              {error && <p className="text-sm text-red-600" role="alert" data-testid="einv-guide-error">{error}</p>}
              <button
                type="submit"
                disabled={busy}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#0066CC] hover:bg-[#0052A3] text-white font-semibold px-6 py-3 rounded-xl transition-colors disabled:opacity-60"
                data-testid="einv-guide-submit"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                {busy ? 'Preparing your PDF...' : 'Download PDF'}
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
};
export default EinvoicingGuideDownload;
